import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, Search, Loader2, ShieldAlert, Trash2, EyeOff, Eye,
  VolumeX, Volume2, Ban, AlertTriangle, Gavel, CheckCircle, XCircle, ChevronRight, X, Clock,
} from 'lucide-react';
import { getDbSafe } from '../services/firebaseConfig';
import { normalizeAuditDoc, type AuditEntry } from '../services/WatcherService';
import { useUserData } from './UserDataContext';
import { toast } from 'sonner';
import { getUserAvatar } from '../utils/userUtils';

// ── Action metadata: icon, label, and filter category per action type ─────────
type Category = 'deleted' | 'hidden_accounts' | 'warnings' | 'appealed' | 'other';
interface ActionMeta { label: string; icon: ReactNode; category: Category; tone: string; }

function getActionMeta(type: string): ActionMeta {
  switch (type) {
    case 'soft_delete_yap': return { label: 'Deleted Yap', icon: <Trash2 className="w-3.5 h-3.5" />, category: 'deleted', tone: '#ef4444' };
    case 'legion_delete': return { label: 'Legion Delete', icon: <Trash2 className="w-3.5 h-3.5" />, category: 'deleted', tone: '#ef4444' };
    case 'hide_content': return { label: 'Hidden Content', icon: <EyeOff className="w-3.5 h-3.5" />, category: 'deleted', tone: '#f97316' };
    case 'hide_account': return { label: 'Hidden Account', icon: <EyeOff className="w-3.5 h-3.5" />, category: 'hidden_accounts', tone: '#f59e0b' };
    case 'unhide_account': return { label: 'Restored Account', icon: <Eye className="w-3.5 h-3.5" />, category: 'hidden_accounts', tone: '#22c55e' };
    case 'warn_user': return { label: 'Warned User', icon: <AlertTriangle className="w-3.5 h-3.5" />, category: 'warnings', tone: '#eab308' };
    case 'mute': return { label: 'Muted', icon: <VolumeX className="w-3.5 h-3.5" />, category: 'other', tone: '#a855f7' };
    case 'unmute': return { label: 'Unmuted', icon: <Volume2 className="w-3.5 h-3.5" />, category: 'other', tone: '#22c55e' };
    case 'ban': return { label: 'Banned', icon: <Ban className="w-3.5 h-3.5" />, category: 'other', tone: '#dc2626' };
    case 'resolve_report': return { label: 'Resolved Report', icon: <CheckCircle className="w-3.5 h-3.5" />, category: 'other', tone: '#16a34a' };
    case 'dismiss_report': return { label: 'Dismissed Report', icon: <XCircle className="w-3.5 h-3.5" />, category: 'other', tone: '#6b7280' };
    case 'override': return { label: 'Overrode Action', icon: <Gavel className="w-3.5 h-3.5" />, category: 'appealed', tone: '#3b82f6' };
    default: return { label: type.replace(/_/g, ' '), icon: <ShieldAlert className="w-3.5 h-3.5" />, category: 'other', tone: '#9ca3af' };
  }
}

interface ResolvedProfile { username: string; name: string; avatar: string; rank: string; }

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts?.toDate === 'function') return ts.toDate().getTime();
  if (typeof ts === 'number') return ts;
  return 0;
}

function relativeTime(ms: number): string {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function exactTime(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const FILTERS: { key: Category | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'deleted', label: 'Deleted Posts' },
  { key: 'hidden_accounts', label: 'Hidden Accounts' },
  { key: 'warnings', label: 'Warnings' },
  { key: 'appealed', label: 'Appealed' },
];

const DATE_RANGES: { key: string; label: string; days: number | null }[] = [
  { key: '7', label: '7 days', days: 7 },
  { key: '30', label: '30 days', days: 30 },
  { key: 'all', label: 'All time', days: null },
];

export function AuditFeed({ onBack }: { onBack: () => void }) {
  const { userProfile } = useUserData();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<Record<string, ResolvedProfile>>({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Category | 'all'>('all');
  const [dateRange, setDateRange] = useState<string>('30');
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const myRank = String((userProfile as any)?.rank || '').toLowerCase();
  const isLegion = myRank === 'legion';

  // Live feed — updates after every watcher move.
  useEffect(() => {
    let unsub: (() => void) | null = null;
    (async () => {
      try {
        const db = await getDbSafe();
        const { collection, query, orderBy, limit, onSnapshot } = await import('firebase/firestore');
        const q = query(collection(db, 'moderation_logs'), orderBy('timestamp', 'desc'), limit(200));
        unsub = onSnapshot(q, snap => {
          setEntries(snap.docs.map(d => normalizeAuditDoc(d.id, d.data())));
          setLoading(false);
        }, () => setLoading(false));
      } catch { setLoading(false); }
    })();
    return () => { unsub?.(); };
  }, []);

  // Lazily resolve usernames/avatars/ranks for every uid referenced.
  useEffect(() => {
    const ids = new Set<string>();
    entries.forEach(e => { if (e.watcher_id) ids.add(e.watcher_id); if (e.target_user_id) ids.add(e.target_user_id); });
    const missing = [...ids].filter(id => id && !profiles[id]);
    if (!missing.length) return;
    (async () => {
      const db = await getDbSafe();
      const { doc, getDoc } = await import('firebase/firestore');
      const updates: Record<string, ResolvedProfile> = {};
      await Promise.all(missing.map(async id => {
        try {
          const s = await getDoc(doc(db, 'users', id));
          if (s.exists()) {
            const d: any = s.data();
            updates[id] = {
              username: d.username || '',
              name: d.name || '',
              avatar: getUserAvatar(d),
              rank: String(d.rank || '').toLowerCase(),
            };
          } else {
            updates[id] = { username: '', name: '', avatar: '', rank: '' };
          }
        } catch { updates[id] = { username: '', name: '', avatar: '', rank: '' }; }
      }));
      setProfiles(prev => ({ ...prev, ...updates }));
    })();
  }, [entries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Map of original log id → override entry, so we can flag appealed actions.
  const overrideByTarget = useMemo(() => {
    const m: Record<string, AuditEntry> = {};
    entries.forEach(e => { const oid = e.metadata?.overriddenLogId; if (e.action_type === 'override' && oid) m[oid] = e; });
    return m;
  }, [entries]);

  // Paged rather than one long scroller. The list is capped at 200 entries and
  // was rendering all of them at once, which is both a wall of text and a lot of
  // DOM for a panel this size.
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(0);

  const modName = (id: string) => profiles[id]?.username ? `@${profiles[id].username.replace(/^@/, '')}` : (id ? id.slice(0, 6) + '…' : 'unknown');
  const targetLabel = (e: AuditEntry) => {
    const snippet = e.metadata?.contentSnippet || e.metadata?.content;
    if (snippet) return `"${String(snippet).slice(0, 60)}${String(snippet).length > 60 ? '…' : ''}"`;
    const p = profiles[e.target_user_id];
    if (p?.username) return `@${p.username.replace(/^@/, '')}`;
    return e.target_user_id ? e.target_user_id.slice(0, 8) + '…' : '—';
  };

  const filtered = useMemo(() => {
    const range = DATE_RANGES.find(r => r.key === dateRange);
    const cutoff = range?.days ? Date.now() - range.days * 86400000 : 0;
    const term = search.trim().toLowerCase();
    return entries.filter(e => {
      const meta = getActionMeta(e.action_type);
      if (filter !== 'all' && meta.category !== filter) return false;
      if (cutoff && tsToMs(e.timestamp) < cutoff) return false;
      if (term) {
        const hay = [
          meta.label, e.action_type, e.reason,
          profiles[e.watcher_id]?.username, profiles[e.target_user_id]?.username,
          profiles[e.target_user_id]?.name, e.metadata?.contentSnippet, e.metadata?.content,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [entries, filter, dateRange, search, profiles]);

  // Back to page 1 whenever the filters change: staying on page 4 of a result
  // set that just became 12 entries long shows an empty list.
  useEffect(() => { setPage(0); }, [dateRange, filtered.length]);

  // ── Detail view ────────────────────────────────────────────────────────────
  if (selected) {
    const meta = getActionMeta(selected.action_type);
    const ms = tsToMs(selected.timestamp);
    const modP = profiles[selected.watcher_id];
    const targetP = profiles[selected.target_user_id];
    const related = entries.filter(e => e.target_user_id && e.target_user_id === selected.target_user_id && e.id !== selected.id);
    const override = selected.id ? overrideByTarget[selected.id] : undefined;
    const snippet = selected.metadata?.contentSnippet || selected.metadata?.content;

    const doOverride = async () => {
      const reason = window.prompt('Override this action — state your reason (logged):');
      if (!reason || reason.trim().length < 4) { if (reason !== null) toast.error('A reason is required.'); return; }
      try {
        const db = await getDbSafe();
        const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
        await addDoc(collection(db, 'moderation_logs'), {
          actorId: String(userProfile?.id || ''),
          actorUsername: String((userProfile as any)?.username || 'legion'),
          targetId: selected.target_user_id,
          actionType: 'override',
          reason: reason.trim(),
          timestamp: serverTimestamp(),
          metadata: { overriddenLogId: selected.id, originalActionType: selected.action_type, originalMod: selected.watcher_id },
        });
        toast.success('Action overridden — logged to the audit feed.');
        setSelected(null);
      } catch { toast.error('Failed to override.'); }
    };

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-1">
          <button onClick={() => setSelected(null)} className="text-xs text-[#DC2626] flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
          <p className="text-white/50 text-xs uppercase tracking-widest font-bold">Action Detail</p>
        </div>

        {/* Header */}
        <div className="rounded-xl p-3 border border-white/8" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-bold text-white" style={{ background: meta.tone }}>
              {meta.icon}{meta.label}
            </span>
            {override && <span className="px-2 py-1 rounded-lg text-[10px] font-bold bg-blue-500/20 text-blue-300">APPEALED</span>}
          </div>
          <p className="text-white/50 text-[11px] flex items-center gap-1.5"><Clock className="w-3 h-3" /> {exactTime(ms)} · {relativeTime(ms)}</p>
        </div>

        {/* Mod */}
        <div className="rounded-xl p-3 border border-white/8 space-y-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-white/40 text-[10px] uppercase tracking-wider font-bold">Actioned by</p>
          <div className="flex items-center gap-2">
            {modP?.avatar ? <img src={modP.avatar} className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-white/10" />}
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold truncate">{modName(selected.watcher_id)}</p>
              {modP?.rank && <p className="text-white/40 text-[10px] uppercase">{modP.rank}</p>}
            </div>
          </div>
        </div>

        {/* Target */}
        <button
          onClick={() => { if (selected.target_user_id) { window.dispatchEvent(new CustomEvent('navigateToFriendProfile', { detail: { id: selected.target_user_id } })); } }}
          className="w-full text-left rounded-xl p-3 border border-white/8 space-y-2 active:scale-[0.99] transition-transform"
          style={{ background: 'rgba(255,255,255,0.04)' }}
        >
          <p className="text-white/40 text-[10px] uppercase tracking-wider font-bold">Target user (tap to view)</p>
          <div className="flex items-center gap-2">
            {targetP?.avatar ? <img src={targetP.avatar} className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-white/10" />}
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-semibold truncate">{targetP?.name || targetLabel(selected)}</p>
              {targetP?.username && <p className="text-white/40 text-[11px] truncate">@{targetP.username.replace(/^@/, '')}</p>}
            </div>
            <ChevronRight className="w-4 h-4 text-white/30" />
          </div>
        </button>

        {/* Reason */}
        <div className="rounded-xl p-3 border border-white/8" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-white/40 text-[10px] uppercase tracking-wider font-bold mb-1">Reason</p>
          <p className="text-white/80 text-[13px] italic">"{selected.reason || 'No reason provided'}"</p>
        </div>

        {/* Content preview / action-specific details */}
        {snippet && (
          <div className="rounded-xl p-3 border border-white/8" style={{ background: 'rgba(0,0,0,0.25)' }}>
            <p className="text-white/40 text-[10px] uppercase tracking-wider font-bold mb-1">Moderated content (read-only)</p>
            <p className="text-white/70 text-[13px] whitespace-pre-wrap break-words">{String(snippet)}</p>
          </div>
        )}
        {selected.action_type === 'hide_account' && (
          <div className="rounded-xl p-3 border border-amber-500/20" style={{ background: 'rgba(245,158,11,0.08)' }}>
            <p className="text-amber-300/90 text-[12px] leading-relaxed">
              Hidden from discovery for a portfolio violation. To be restored, the user must update their portfolio photos
              and contact the actioning moderator ({modName(selected.watcher_id)}), who can Unhide the account.
            </p>
          </div>
        )}

        {/* Related actions on this user */}
        <div className="rounded-xl p-3 border border-white/8" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-white/40 text-[10px] uppercase tracking-wider font-bold mb-1.5">
            Related actions on this user {related.length > 0 && `(${related.length})`}
          </p>
          {related.length === 0 ? (
            <p className="text-white/30 text-[12px]">No other actions on this user.</p>
          ) : (
            <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
              {related.slice(0, 10).map((r, i) => {
                const rm = getActionMeta(r.action_type);
                return (
                  <button key={r.id || i} onClick={() => setSelected(r)} className="w-full flex items-center gap-2 text-left">
                    <span style={{ color: rm.tone }}>{rm.icon}</span>
                    <span className="text-white/70 text-[11px] flex-1 truncate">{rm.label} — "{r.reason}"</span>
                    <span className="text-white/30 text-[10px] shrink-0">{relativeTime(tsToMs(r.timestamp))}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Legion override */}
        {isLegion && selected.action_type !== 'override' && !override && (
          <button onClick={doOverride} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold text-white active:scale-95 transition-transform" style={{ background: '#1d4ed8' }}>
            <Gavel className="w-3.5 h-3.5" /> Override this action
          </button>
        )}
        {override && (
          <div className="rounded-xl p-3 border border-blue-500/20" style={{ background: 'rgba(59,130,246,0.08)' }}>
            <p className="text-blue-300/90 text-[12px]">Overridden by {modName(override.watcher_id)} — "{override.reason}"</p>
          </div>
        )}
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <button onClick={onBack} className="text-xs text-[#DC2626] flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
        <p className="text-white/50 text-xs uppercase tracking-widest font-bold">Audit Feed</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search mod, user, or action…"
          className="w-full pl-8 pr-2 py-1.5 rounded-lg text-xs text-white placeholder:text-white/30 outline-none"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
        />
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-0.5 px-0.5">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key as any)}
            className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${filter === f.key ? 'bg-[#DC2626] text-white' : 'bg-white/8 text-white/50 border border-white/12'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Date range */}
      <div className="flex gap-1.5">
        {DATE_RANGES.map(r => (
          <button key={r.key} onClick={() => setDateRange(r.key)}
            className={`px-2 py-0.5 rounded-md text-[10px] font-semibold transition-all ${dateRange === r.key ? 'bg-white/15 text-white' : 'text-white/40'}`}>
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#DC2626] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-white/30 text-sm text-center py-8">No actions match.</p>
      ) : (
        <div
          className="space-y-2 overflow-y-auto pr-1"
          // Inline: max-h-80 has no rule in the committed pre-compiled
          // index.css, so this list had NO height cap and ran off the top of
          // the screen inside the remote.
          style={{ maxHeight: '46vh' }}
        >
          <AnimatePresence initial={false}>
            {filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((e, i) => {
              const meta = getActionMeta(e.action_type);
              const ms = tsToMs(e.timestamp);
              const modP = profiles[e.watcher_id];
              const appealed = e.id ? !!overrideByTarget[e.id] : false;
              return (
                <motion.button
                  key={e.id || i}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  onClick={() => setSelected(e)}
                  className="w-full text-left rounded-xl px-3 py-2.5 border border-white/8 active:scale-[0.99] transition-transform"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold text-white shrink-0" style={{ background: meta.tone }}>
                      {meta.icon}{meta.label}
                    </span>
                    {appealed && <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-blue-500/20 text-blue-300 shrink-0">APPEALED</span>}
                    <span className="text-white/30 text-[10px] ml-auto shrink-0">{exactTime(ms)}</span>
                  </div>
                  <p className="text-white/70 text-[12px] truncate">
                    {targetLabel(e)}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1">
                    {modP?.avatar ? <img src={modP.avatar} className="w-4 h-4 rounded-full object-cover" /> : <div className="w-4 h-4 rounded-full bg-white/10" />}
                    <span className="text-white/40 text-[11px]">{modName(e.watcher_id)}</span>
                    {e.reason && <span className="text-white/30 text-[11px] truncate">· {e.reason}</span>}
                  </div>
                </motion.button>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Pager. Hidden when everything fits on one page, so a short feed does not
          grow a control that does nothing. */}
      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1.5 rounded-lg disabled:opacity-40"
            style={{ fontSize: 11, fontWeight: 700, border: '1px solid rgba(127,127,127,0.35)', color: 'inherit' }}
          >
            Previous
          </button>
          <span style={{ fontSize: 11, opacity: 0.6 }}>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <button
            onClick={() => setPage(p => ((p + 1) * PAGE_SIZE < filtered.length ? p + 1 : p))}
            disabled={(page + 1) * PAGE_SIZE >= filtered.length}
            className="px-3 py-1.5 rounded-lg disabled:opacity-40"
            style={{ fontSize: 11, fontWeight: 700, border: '1px solid rgba(127,127,127,0.35)', color: 'inherit' }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
